import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, LinkedinIcon, MapPinIcon, CheckCircleIcon, SendIcon } from 'lucide-react';
import { ThreeBackground } from '../components/ThreeBackground';
import { PageTransition } from '../components/PageTransition';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const submitData = new FormData();
      submitData.append('q3_fullName', formData.name);
      submitData.append('q4_email', formData.email);
      submitData.append('q5_subject', formData.subject);
      submitData.append('q6_message', formData.message);
      await fetch('https://submit.jotform.com/submit/260780068773061', {
        method: 'POST',
        body: submitData,
        mode: 'no-cors'
      });
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setStatus('idle'), 5000);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  return <PageTransition data-id="element-92">
      {/* Hero Header */}
      <section className="relative bg-navy-900 pt-32 pb-28 overflow-hidden bg-glow" data-id="element-93">
        <ThreeBackground data-id="element-94" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center" data-id="element-95">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-96">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-97">
              Connect
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-98">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl mx-auto" data-id="element-99">
              Interested in discussing executive leadership opportunities in
              midstream operations, gas processing, or industrial management? I
              welcome the opportunity to connect.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-light py-20" data-id="element-100">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center" data-id="element-101">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16" data-id="element-102">
            <a href="mailto:dennissorce7@gmail.com" className="flex items-center justify-center px-10 py-5 bg-gold text-navy-900 font-bold tracking-wide text-lg hover:bg-gold-light transition-colors duration-300 w-full sm:w-auto rounded" data-id="element-103">
              <MailIcon className="mr-3" size={22} data-id="element-104" />
              Email Bryan
            </a>
            <a href="https://www.linkedin.com/in/bryan-nicholson-b5320b48" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center px-10 py-5 border-2 border-navy-900 text-navy-900 font-bold tracking-wide text-lg hover:bg-navy-900 hover:text-white transition-colors duration-300 w-full sm:w-auto rounded" data-id="element-105">
              <LinkedinIcon className="mr-3" size={22} data-id="element-106" />
              LinkedIn Profile
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex items-center justify-center text-ink-muted font-medium text-lg mb-20" data-id="element-107">
            <MapPinIcon className="text-gold mr-3" size={22} data-id="element-108" />
            Based in Texas, United States
          </motion.div>

          {/* Contact Form Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="max-w-xl mx-auto bg-white p-8 md:p-10 rounded-xl shadow-sm border border-gray-100 text-left" data-id="element-109">
            <div className="text-center mb-8" data-id="element-110">
              <h2 className="text-2xl font-bold text-ink mb-2" data-id="element-111">
                Send a Message
              </h2>
              <p className="text-ink-muted" data-id="element-112">
                Fill out the form below and I'll get back to you shortly.
              </p>
            </div>

            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center justify-center py-12 text-center" data-id="element-113">
                <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-4" data-id="element-114">
                  <CheckCircleIcon size={32} data-id="element-115" />
                </div>
                <h3 className="text-xl font-bold text-ink mb-2" data-id="element-116">
                  Message Sent!
                </h3>
                <p className="text-ink-muted" data-id="element-117">
                  Thank you for reaching out. I will review your message and
                  respond soon.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-118">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-119">
                  <div data-id="element-120">
                    <label htmlFor="name" className="block text-sm font-medium text-ink mb-1.5" data-id="element-121">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold/30 outline-none transition-all bg-gray-50/50 focus:bg-white text-ink" placeholder="John Doe" data-id="element-122" />
                  </div>
                  <div data-id="element-123">
                    <label htmlFor="email" className="block text-sm font-medium text-ink mb-1.5" data-id="element-124">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold/30 outline-none transition-all bg-gray-50/50 focus:bg-white text-ink" placeholder="john@example.com" data-id="element-125" />
                  </div>
                </div>

                <div data-id="element-126">
                  <label htmlFor="subject" className="block text-sm font-medium text-ink mb-1.5" data-id="element-127">
                    Subject
                  </label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 rounded border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold/30 outline-none transition-all bg-gray-50/50 focus:bg-white text-ink" placeholder="How can we collaborate?" data-id="element-128" />
                </div>

                <div data-id="element-129">
                  <label htmlFor="message" className="block text-sm font-medium text-ink mb-1.5" data-id="element-130">
                    Message *
                  </label>
                  <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold/30 outline-none transition-all bg-gray-50/50 focus:bg-white text-ink resize-none" placeholder="Your message here..." data-id="element-131" />
                </div>

                <button type="submit" disabled={status === 'submitting'} className="w-full flex items-center justify-center py-4 bg-gold text-navy-900 font-bold tracking-wide hover:bg-gold-light transition-colors duration-300 rounded disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-132">
                  {status === 'submitting' ? <span className="flex items-center" data-id="element-133">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-navy-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-134">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-135"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-136"></path>
                      </svg>
                      Sending...
                    </span> : <span className="flex items-center" data-id="element-137">
                      <SendIcon className="mr-2" size={18} data-id="element-138" />
                      Send Message
                    </span>}
                </button>
              </form>}
          </motion.div>

          {/* Additional context */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mt-20 pt-16 border-t border-gray-200" data-id="element-139">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-140">
              Areas of Interest
            </h2>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-141">
              <span className="px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded" data-id="element-142">
                Midstream Operations
              </span>
              <span className="px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded" data-id="element-143">
                Gas Processing
              </span>
              <span className="px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded" data-id="element-144">
                Plant Management
              </span>
              <span className="px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded" data-id="element-145">
                Executive Leadership
              </span>
              <span className="px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded" data-id="element-146">
                Board Advisory
              </span>
              <span className="px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded" data-id="element-147">
                Capital Projects
              </span>
            </div>
          </motion.div>
        </div>
      </section>
    </PageTransition>;
}